import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import styled, { css, keyframes } from 'styled-components';
import { shouldForwardProp } from '../../utils/styled-helpers';

export type ToastType = 'info' | 'success' | 'warning' | 'error';
export type ToastPosition =
  | 'top-right'
  | 'top-left'
  | 'bottom-right'
  | 'bottom-left'
  | 'top-center'
  | 'bottom-center';

export interface ToastProps {
  id: string;
  type?: ToastType;
  title?: string;
  message: string;
  duration?: number;
  position?: ToastPosition;
  onClose?: (id: string) => void;
  showIcon?: boolean;
  showCloseButton?: boolean;
  isVisible?: boolean;
}

interface ToastContainerProps {
  position: ToastPosition;
}

// 애니메이션 효과
const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const fadeInDown = keyframes`
  from {
    opacity: 0;
    transform: translateY(-20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const fadeInRight = keyframes`
  from {
    opacity: 0;
    transform: translateX(-20px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

const fadeInLeft = keyframes`
  from {
    opacity: 0;
    transform: translateX(20px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

const fadeOut = keyframes`
  from {
    opacity: 1;
  }
  to {
    opacity: 0;
  }
`;

// 위치별 스타일
const positionStyles = {
  'top-right': css`
    top: 20px;
    right: 20px;
    animation: ${fadeInLeft} 0.3s ease-out;
  `,
  'top-left': css`
    top: 20px;
    left: 20px;
    animation: ${fadeInRight} 0.3s ease-out;
  `,
  'bottom-right': css`
    bottom: 20px;
    right: 20px;
    animation: ${fadeInLeft} 0.3s ease-out;
  `,
  'bottom-left': css`
    bottom: 20px;
    left: 20px;
    animation: ${fadeInRight} 0.3s ease-out;
  `,
  'top-center': css`
    top: 20px;
    left: 50%;
    transform: translateX(-50%);
    animation: ${fadeInDown} 0.3s ease-out;
  `,
  'bottom-center': css`
    bottom: 20px;
    left: 50%;
    transform: translateX(-50%);
    animation: ${fadeInUp} 0.3s ease-out;
  `,
};

// 타입별 색상 스타일
const typeStyles = {
  info: css`
    background-color: #ebf5ff;
    border-left: 4px solid #3b82f6;

    .toast-icon {
      color: #3b82f6;
    }
  `,
  success: css`
    background-color: #ecfdf5;
    border-left: 4px solid #10b981;

    .toast-icon {
      color: #10b981;
    }
  `,
  warning: css`
    background-color: #fffbeb;
    border-left: 4px solid #f59e0b;

    .toast-icon {
      color: #f59e0b;
    }
  `,
  error: css`
    background-color: #fef2f2;
    border-left: 4px solid #ef4444;

    .toast-icon {
      color: #ef4444;
    }
  `,
};

const ToastContainer = styled.div.withConfig({
  shouldForwardProp: shouldForwardProp(['position']),
})<ToastContainerProps>`
  position: fixed;
  z-index: 9999;
  display: flex;
  flex-direction: column;
  gap: 10px;
  max-width: 100%;

  ${(props) => positionStyles[props.position]}
`;

const ToastItem = styled.div.withConfig({
  shouldForwardProp: shouldForwardProp(['type', 'isExiting']),
})<{ type: ToastType; isExiting: boolean }>`
  position: relative;
  width: 320px;
  max-width: calc(100vw - 40px);
  padding: 12px 16px;
  border-radius: 4px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  display: flex;
  align-items: flex-start;

  ${(props) => typeStyles[props.type]}

  animation: ${(props) => (props.isExiting ? fadeOut : 'none')} 0.3s ease-out forwards;
`;

const ToastContent = styled.div`
  flex: 1;
  margin: 0 12px;
`;

const ToastTitle = styled.h4`
  margin: 0 0 4px 0;
  font-size: 1rem;
  font-weight: 600;
  color: var(--ws-ink);
`;

const ToastMessage = styled.p`
  margin: 0;
  font-size: 0.875rem;
  color: var(--ws-ink-2);
`;

const IconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 20px;
  height: 20px;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 10px;
  right: 10px;
  border: none;
  background: none;
  color: var(--ws-ink-4);
  cursor: pointer;
  width: 20px;
  height: 20px;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;

  &:hover {
    color: var(--ws-ink);
    background-color: rgba(0, 0, 0, 0.05);
  }
`;

// 아이콘 컴포넌트들
const InfoIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22ZM12 10C12.5523 10 13 10.4477 13 11V17C13 17.5523 12.5523 18 12 18C11.4477 18 11 17.5523 11 17V11C11 10.4477 11.4477 10 12 10ZM12 9C12.5523 9 13 8.55228 13 8C13 7.44772 12.5523 7 12 7C11.4477 7 11 7.44772 11 8C11 8.55228 11.4477 9 12 9Z"
      fill="currentColor"
    />
  </svg>
);

const SuccessIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22ZM16.7071 10.7071C17.0976 10.3166 17.0976 9.68342 16.7071 9.29289C16.3166 8.90237 15.6834 8.90237 15.2929 9.29289L11 13.5858L8.70711 11.2929C8.31658 10.9024 7.68342 10.9024 7.29289 11.2929C6.90237 11.6834 6.90237 12.3166 7.29289 12.7071L10.2929 15.7071C10.6834 16.0976 11.3166 16.0976 11.7071 15.7071L16.7071 10.7071Z"
      fill="currentColor"
    />
  </svg>
);

const WarningIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M9.40193 3.26967C10.5552 1.33301 13.4448 1.33301 14.5981 3.26967L21.9677 15.5272C23.1457 17.5161 21.6734 20 19.3696 20H4.63039C2.32658 20 0.85427 17.5161 2.03234 15.5272L9.40193 3.26967ZM12 8C12.5523 8 13 8.44772 13 9V13C13 13.5523 12.5523 14 12 14C11.4477 14 11 13.5523 11 13V9C11 8.44772 11.4477 8 12 8ZM12 17C12.5523 17 13 16.5523 13 16C13 15.4477 12.5523 15 12 15C11.4477 15 11 15.4477 11 16C11 16.5523 11.4477 17 12 17Z"
      fill="currentColor"
    />
  </svg>
);

const ErrorIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22ZM8.96967 8.96967C9.26256 8.67678 9.73744 8.67678 10.0303 8.96967L12 10.9393L13.9697 8.96967C14.2626 8.67678 14.7374 8.67678 15.0303 8.96967C15.3232 9.26256 15.3232 9.73744 15.0303 10.0303L13.0607 12L15.0303 13.9697C15.3232 14.2626 15.3232 14.7374 15.0303 15.0303C14.7374 15.3232 14.2626 15.3232 13.9697 15.0303L12 13.0607L10.0303 15.0303C9.73744 15.3232 9.26256 15.3232 8.96967 15.0303C8.67678 14.7374 8.67678 14.2626 8.96967 13.9697L10.9393 12L8.96967 10.0303C8.67678 9.73744 8.67678 9.26256 8.96967 8.96967Z"
      fill="currentColor"
    />
  </svg>
);

const CloseIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M18 6L6 18M6 6L18 18"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/**
 * 토스트 컴포넌트
 *
 * 화면에 일시적으로 표시되는 알림 메시지를 위한 컴포넌트입니다.
 */
const Toast: React.FC<ToastProps> = ({
  id,
  type = 'info',
  title,
  message,
  duration = 3000,
  position = 'top-right',
  onClose,
  showIcon = true,
  showCloseButton = true,
  isVisible = true,
}) => {
  const [isExiting, setIsExiting] = useState(false);

  // 토스트 닫기 함수
  const closeToast = useCallback(() => {
    setIsExiting(true);

    // 애니메이션 후 onClose 콜백 호출
    setTimeout(() => {
      if (onClose) {
        onClose(id);
      }
    }, 300); // 애니메이션 시간
  }, [id, onClose]);

  // 자동 닫기 타이머
  useEffect(() => {
    if (duration && isVisible) {
      const timer = setTimeout(() => {
        closeToast();
      }, duration);

      return () => {
        clearTimeout(timer);
      };
    }
  }, [duration, isVisible, closeToast]);

  // 타입별 아이콘 선택
  const getIcon = () => {
    switch (type) {
      case 'info':
        return <InfoIcon />;
      case 'success':
        return <SuccessIcon />;
      case 'warning':
        return <WarningIcon />;
      case 'error':
        return <ErrorIcon />;
      default:
        return <InfoIcon />;
    }
  };

  // Portal을 사용하여 body에 직접 렌더링
  return createPortal(
    <ToastContainer position={position}>
      <ToastItem type={type} isExiting={isExiting}>
        {showIcon && <IconWrapper className="toast-icon">{getIcon()}</IconWrapper>}

        <ToastContent>
          {title && <ToastTitle>{title}</ToastTitle>}
          <ToastMessage>{message}</ToastMessage>
        </ToastContent>

        {showCloseButton && (
          <CloseButton onClick={closeToast} aria-label="닫기">
            <CloseIcon />
          </CloseButton>
        )}
      </ToastItem>
    </ToastContainer>,
    document.body,
  );
};

export default Toast;
